import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormsModule, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatRadioChange, MatRadioModule } from '@angular/material/radio';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import {
    AdyenRegisterCardRequest,
    CartDescriptionException,
    CartExceptionType,
    PaymentMethods,
    PaymentMethodsType,
} from '@cargos/sprintpay-models';
import { AdvancedFlowApiService, HideDownloadDirective, TokenizedCardPayload } from '@cargos/sprintpay-ui';
import { map, of, Subject, switchMap, take, takeUntil } from 'rxjs';
import { CARD_TYPES_MAP, ConfigService, PaymentCartService, PaymentMethodsService } from 'src/app/shared/services';
import Swal from 'sweetalert2';
import { PaymentMethodGuest } from '../../shared/models/payment-method.model';
import { PaymentMethodsHandlerService } from '../../shared/services/payment-methods-handler.service';
import { LastFourDigitsComponent } from '../last-four-digits/last-four-digits.component';
import { CardTypeLibComponent } from '../logo-credit-card/card-type.component';
import { LogoPaymentMethodComponent } from '../logo-payment-method/logo-payment-method.component';
import { PaymentMethodSelectedFormGroupType } from './models/payment-selected-form';

@Component({
    selector: 'app-guest-payment-method',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        FormsModule,
        LastFourDigitsComponent,
        MatDividerModule,
        MatRadioModule,
        LogoPaymentMethodComponent,
        MatIconModule,
        MatButtonModule,
        MatTooltipModule,
        CardTypeLibComponent,
        HideDownloadDirective,
    ],
    templateUrl: './guest-payment-method.component.html',
    styleUrls: ['./guest-payment-method.component.scss'],
})
export class GuestPaymentMethodComponent implements OnInit, OnDestroy {
    private unsubscribe$: Subject<void>;

    public paymentMethodGuest?: PaymentMethodGuest | null;
    public lastFourDigitsCreditCard = '';
    public lastFourDigitsEcheck: string | undefined = '';
    public cardType = '';
    public showError = false;
    public msgError = '';
    public paymentMethodSelectedForm: PaymentMethodSelectedFormGroupType;
    public PaymentMethods = PaymentMethods;
    public isAGIWalletEnable = false;
    public helpInfoAgiWallet =
        'Please note in the AGI Wallet hosted by CargoSprint - SprintPay users will not be charged a 3rd Party fee unless SprintPay Credit is used. Bank Account (ACH), Credit Card and other options hosted in the wallet will not have a CargoSprint fee added to checkout.';
    public isEcheckEnableAsGuestPayment = false;
    public creditCardNonce?: TokenizedCardPayload;
    public echeckOTToken: string | undefined = '';
    public paymentVehicle?: AdyenRegisterCardRequest['PaymentVehicle'];

    get paymentMethodSelected(): AbstractControl<PaymentMethodsType> | null {
        return this.paymentMethodSelectedForm.get('paymentSelected');
    }

    private advancedFlowApiService = inject(AdvancedFlowApiService);

    constructor(
        private paymentCartService: PaymentCartService,
        private formBuilder: NonNullableFormBuilder,
        private configService: ConfigService,
        private paymentMethodsService: PaymentMethodsService,
        private router: Router,
        private paymentMethodsHandlerService: PaymentMethodsHandlerService
    ) {
        this.unsubscribe$ = new Subject<void>();
    }

    ngOnInit(): void {
        this.getConfig();
        this.subscribePaymentMethods();
        this.subscribeGuestCreditCard();
        this.subscribeEventOnTryPayment();
        this.subscribeGuestPaymentMethod();

        if (this.isEcheckEnableAsGuestPayment) {
            this.subscribeGuestEcheck();
        }

        this.createForm();
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    subscribeEventOnTryPayment(): void {
        this.paymentCartService
            .getSubmitAccures()
            .pipe(
                switchMap((submitted: boolean) => {
                    if (submitted) {
                        return this.paymentMethodsHandlerService.isPaymentMethodGuestValid$().pipe(
                            map((valid: boolean) => {
                                return !valid;
                            })
                        );
                    }
                    return of(false);
                }),
                takeUntil(this.unsubscribe$)
            )
            .subscribe({
                next: (invalidPaymentMethod: boolean) => {
                    if (this.paymentMethodSelected?.value === PaymentMethods.CREDIT_CARD) {
                        this.msgError = CartDescriptionException[CartExceptionType.CREDIT_CARD_FORM_INVALID];
                    }

                    if (this.paymentMethodSelected?.value === PaymentMethods.ECHECK) {
                        this.msgError = CartDescriptionException[CartExceptionType.PAYMENT_METHOD_INVALID];
                    }
                    this.showError = invalidPaymentMethod;
                },
            });
    }

    // TODO: This function should be remplaced with another funtion that uses isPaymentMethodGuestValid() and receive just the form after getPaymentMethodGuest() is refactored
    subscribeGuestCreditCard(): void {
        this.paymentMethodsHandlerService
            .getGuestCreditCard()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (paymentMethod) => {
                    this.lastFourDigitsCreditCard = paymentMethod?.creditCardNonce?.details?.lastFour || '';
                    this.cardType = paymentMethod?.creditCardNonce?.details?.cardType || '';
                    this.creditCardNonce = paymentMethod?.creditCardNonce;

                    if (paymentMethod?.paymentVehicle && paymentMethod.paymentVehicle.PaymentSourceSubtype) {
                        paymentMethod.paymentVehicle.PaymentSourceSubtype =
                            CARD_TYPES_MAP[paymentMethod.paymentVehicle.PaymentSourceSubtype] ||
                            paymentMethod.paymentVehicle.PaymentSourceSubtype;
                        this.paymentVehicle = paymentMethod.paymentVehicle;
                    }
                },
            });
    }

    removeGuestPaymentMethod(): void {
        Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            showCancelButton: true,
            showDenyButton: true,
            showConfirmButton: false,
            denyButtonText: 'Yes, delete it!',
            reverseButtons: true,
        }).then((result) => {
            if (result.isDenied) {
                this.paymentVehicle = undefined;
                this.paymentMethodsHandlerService.removePaymentMethodGuest();
                this.advancedFlowApiService.setAdyenRegisterCardRequest(null);
                if (this.paymentMethodSelected?.value === PaymentMethods.CREDIT_CARD) {
                    this.paymentMethodsHandlerService.resetGuestCreditCard();
                }
                if (this.paymentMethodSelected?.value === PaymentMethods.ECHECK) {
                    this.paymentMethodsHandlerService.resetGuestEcheck();
                }

                Swal.fire('Removed!', '', 'success');
            }
        });
    }

    createForm(): void {
        this.paymentMethodSelectedForm = this.formBuilder.group({
            paymentSelected: [
                (this.paymentMethodGuest?.paymentMethod || PaymentMethods.CREDIT_CARD) as PaymentMethodsType,
                Validators.required,
            ],
        });
    }

    setPaymentMethodGuest(paymentMethodGuest: PaymentMethodGuest): void {
        this.paymentMethodsHandlerService.setPaymentMethodGuest(paymentMethodGuest);
    }

    onSelectPaymentMethod(event: MatRadioChange): void {
        this.setPaymentMethodGuest({
            paymentMethod: event.value,
        });
    }

    selectSprintPay(): void {
        this.router.navigate(['/login']);
    }

    subscribePaymentMethods(): void {
        this.paymentMethodsService.getPaymentMethodsRequest$().pipe(take(1)).subscribe();
    }

    getConfig(): void {
        this.isAGIWalletEnable = this.configService.isAGIWalletEnable();
        this.isEcheckEnableAsGuestPayment = this.configService.isEcheckEnableAsGuestPayment();
    }

    subscribeGuestEcheck(): void {
        this.paymentMethodsHandlerService
            .getGuestEcheck()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((paymentMethod: PaymentMethodGuest | null) => {
                this.lastFourDigitsEcheck = this.paymentMethodGuest?.echeckForm
                    ?.getRawValue()
                    ?.reRoutingNumber?.slice(-4);
                this.echeckOTToken = paymentMethod?.echeckOTToken;
            });
    }

    redirectToEcheckFormRoute(): void {
        this.setPaymentMethodGuest({
            paymentMethod: PaymentMethods.ECHECK,
        });
        this.router.navigate(['./cart/echeck']);
    }

    setCreditCardAsPaymentMethodAndRedirect(): void {
        this.advancedFlowApiService.setPaymentMethod('Cards');
        this.setPaymentMethodGuest({
            paymentMethod: PaymentMethods.CREDIT_CARD,
        });
        this.redirectToCreditCardFormRoute();
    }

    redirectToCreditCardFormRoute(): void {
        this.router.navigate(['./cart/payment-method']);
    }

    subscribeGuestPaymentMethod(): void {
        this.paymentMethodsHandlerService
            .getPaymentMethodGuest()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((paymentMethod: PaymentMethodGuest | null) => {
                this.paymentMethodGuest = paymentMethod;
            });
    }
}
